import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowRight, Play } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

const TYPE_COLORS = {
  pv: 'bg-[var(--energy-green-soft)] text-[var(--energy-green)]',
  ev: 'bg-blue-50 text-blue-700',
  repairs: 'bg-amber-50 text-amber-700',
};

export default function Projects() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('all');

  const allProjects = t('projects_page.projects', { returnObjects: true }) || [];
  const filtered = activeFilter === 'all'
    ? allProjects
    : allProjects.filter((p) => p.type === activeFilter);

  const filters = [
    { key: 'all', label: t('projects_page.filter_all') },
    { key: 'pv', label: t('projects_page.filter_pv') },
    { key: 'ev', label: t('projects_page.filter_ev') },
    { key: 'repairs', label: t('projects_page.filter_repairs') },
  ];

  return (
    <>
      <Helmet>
        <title>Projects — ENERGIESSE SL Solar Installation Portfolio</title>
        <meta
          name="description"
          content="Portfolio of residential PV, EV charger, and repair projects. Before/after comparisons. All work delivered as B2B subcontractor."
        />
      </Helmet>

      {/* Page Hero */}
      <section className="pt-14 pb-10 bg-[var(--energy-bg)] border-b border-border">
        <div className="container-site">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl space-y-4"
          >
            <motion.p variants={fadeUp} className="text-xs font-medium text-[var(--energy-green)] uppercase tracking-widest">
              {t('nav.projects')}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-heading text-3xl md:text-4xl font-semibold tracking-tight"
            >
              {t('projects_page.headline')}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base text-muted-foreground leading-relaxed max-w-[60ch]">
              {t('projects_page.subtitle')}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Filter + Grid */}
      <section className="section-padding bg-white">
        <div className="container-site space-y-8">
          {/* Filter row */}
          <div className="flex flex-wrap gap-2" data-testid="project-filters">
            {filters.map((filter) => (
              <button
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                className={`px-4 py-1.5 text-sm font-medium rounded-full border transition-colors ${
                  activeFilter === filter.key
                    ? 'bg-[var(--energy-green)] text-white border-[var(--energy-green)]'
                    : 'bg-white text-muted-foreground border-border hover:border-foreground/20 hover:text-foreground'
                }`}
                data-testid={`filter-${filter.key}`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          <motion.div
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            data-testid="projects-gallery-grid"
          >
            {filtered.map((project, i) => (
              <motion.div
                key={project.id}
                layout
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
                className="border border-border rounded-2xl overflow-hidden card-hover bg-white"
                data-testid={`project-card-${project.id}`}
              >
                {/* Before/After Tabs */}
                <Tabs defaultValue="after" className="w-full">
                  <div className="relative">
                    <TabsContent value="before" className="m-0">
                      <img
                        src={project.before_img}
                        alt={`${project.title} - before`}
                        className="w-full h-52 object-cover"
                        loading="lazy"
                      />
                    </TabsContent>
                    <TabsContent value="after" className="m-0">
                      <img
                        src={project.after_img}
                        alt={`${project.title} - after`}
                        className="w-full h-52 object-cover"
                        loading="lazy"
                      />
                    </TabsContent>
                    <div className="absolute top-3 right-3">
                      <TabsList className="h-7 bg-black/50 backdrop-blur-sm border-0" data-testid="project-before-after">
                        <TabsTrigger value="before" className="text-[10px] text-white data-[state=active]:bg-white data-[state=active]:text-foreground h-5 px-2">
                          {t('projects_page.before')}
                        </TabsTrigger>
                        <TabsTrigger value="after" className="text-[10px] text-white data-[state=active]:bg-white data-[state=active]:text-foreground h-5 px-2">
                          {t('projects_page.after')}
                        </TabsTrigger>
                      </TabsList>
                    </div>
                    <div className="absolute top-3 left-3">
                      <Badge
                        className={`text-[10px] font-medium border-0 ${TYPE_COLORS[project.type] || 'bg-muted text-muted-foreground'}`}
                      >
                        {project.type.toUpperCase()}
                      </Badge>
                    </div>
                  </div>

                  {/* Card Info */}
                  <div className="p-4 space-y-3">
                    <h3 className="font-heading font-semibold text-sm">{project.title}</h3>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                      {[
                        { label: 'Capacity', value: project.capacity },
                        { label: 'Location', value: project.location },
                        { label: 'Year', value: project.year },
                        { label: 'Client', value: project.client },
                      ].map((info, ii) => (
                        <div key={ii}>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{info.label}</p>
                          <p className="text-xs font-medium">{info.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </Tabs>
              </motion.div>
            ))}
          </motion.div>

          {/* Video placeholder */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="rounded-2xl border-2 border-dashed border-border bg-muted/30 flex flex-col items-center justify-center py-16 gap-3"
          >
            <div className="w-14 h-14 rounded-full bg-[var(--energy-green-soft)] flex items-center justify-center">
              <Play className="h-6 w-6 text-[var(--energy-green)] ml-0.5" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">{t('projects_page.video_placeholder')}</p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-[hsl(222,22%,10%)]">
        <div className="container-site">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-1">
              <h2 className="font-heading text-xl font-semibold text-white">{t('cta_section.title')}</h2>
              <p className="text-sm text-[hsl(215,12%,55%)]">{t('cta_section.subtitle')}</p>
            </div>
            <Button
              size="lg"
              className="bg-[var(--energy-green)] hover:bg-[var(--energy-green)]/90 text-white btn-primary flex-shrink-0"
              onClick={() => navigate('/contact')}
              data-testid="projects-cta-button"
            >
              {t('cta_section.cta')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
